const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete'])
const STORAGE_KEY = 'melcaisse:idempotency-requests'
const REUSE_WINDOW_MS = 15000

const uuid = () => globalThis.crypto?.randomUUID
  ? globalThis.crypto.randomUUID()
  : `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`

const stableValue = (value) => {
  if (value instanceof FormData) {
    return [...value.entries()].map(([key, item]) => [
      key,
      item instanceof File
        ? { name: item.name, size: item.size, type: item.type, lastModified: item.lastModified }
        : item,
    ]).sort(([left], [right]) => left.localeCompare(right))
  }
  if (Array.isArray(value)) return value.map(stableValue)
  if (value && typeof value === 'object') {
    return Object.keys(value).sort().reduce((result, key) => {
      result[key] = stableValue(value[key])
      return result
    }, {})
  }
  return value ?? null
}

const readEntries = () => {
  try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}') } catch { return {} }
}

const fingerprint = (config) => JSON.stringify({
  method: String(config.method || 'get').toLowerCase(),
  url: config.url,
  user: String(config.headers?.Authorization || localStorage.getItem('jwtToken') || '').slice(-24),
  data: stableValue(config.data),
})

export default function installIdempotency(axios) {
  axios.interceptors.request.use((config) => {
    const method = String(config.method || 'get').toLowerCase()
    if (!MUTATING_METHODS.has(method) || config.skipIdempotency) return config

    config.headers = config.headers || {}
    if (config.headers['Idempotency-Key']) return config

    const signature = fingerprint(config)
    const now = Date.now()
    const entries = readEntries()
    Object.keys(entries).forEach((key) => {
      if (!entries[key]?.expiresAt || entries[key].expiresAt <= now) delete entries[key]
    })

    const key = entries[signature]?.key || uuid()
    entries[signature] = { key, expiresAt: now + REUSE_WINDOW_MS }
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entries)) } catch {}
    config.headers['Idempotency-Key'] = key
    return config
  })

  axios.interceptors.response.use(async (response) => {
    const processing = response.status === 202 && response.headers?.['idempotency-status'] === 'processing'
    const attempts = response.config.__idempotencyPollAttempts || 0
    if (!processing || attempts >= 15) return response

    await new Promise((resolve) => setTimeout(resolve, 500))
    return axios({
      ...response.config,
      __idempotencyPollAttempts: attempts + 1,
      headers: { ...response.config.headers },
    })
  })
}
